// home-gsap.js
(() => {
  const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduceMotion) return;

  // ---- section reveal tweaks ----
  const FROM_OPACITY = 0;
  const FROM_Y = 22;
  const IN_DURATION = 0.85;
  const EASE = "power2.out";

  const PENDING_CLASS = "gsap-pending";

  function waitForGSAP(cb) {
    let tries = 0;
    const t = setInterval(() => {
      if (window.gsap) {
        clearInterval(t);
        cb();
      } else if (++tries > 80) {
        clearInterval(t);
        document.documentElement.classList.remove(PENDING_CLASS);
      }
    }, 50);
  }

  function getMain() {
    return document.querySelector("#MainContent") || document.querySelector("main");
  }

  function getSections(main) {
    const sections = Array.from(main.querySelectorAll(".shopify-section")).filter(
      (el) => el instanceof HTMLElement
    );
    return sections.filter((el) => el.offsetHeight > 40);
  }

  function state(el) {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || 800;
    if (r.bottom <= 0) return "above";
    if (r.top >= vh) return "below";
    return "in";
  }

  function cleanup(scope = document) {
    // Kill ScrollTriggers we made
    if (window.ScrollTrigger) {
      ScrollTrigger.getAll().forEach((t) => {
        if (String(t.vars?.id || "").startsWith("home:")) t.kill(true);
      });
    }

    // Remove buddy (Shopify editor reloads)
    document.querySelectorAll(".home-logo-buddy").forEach((n) => n.remove());

    const main = scope.querySelector("#MainContent") || scope.querySelector("main") || scope;

    main.querySelectorAll("[data-gsap-ready]").forEach((el) =>
      el.removeAttribute("data-gsap-ready")
    );

    // Reset only what we set
    main.querySelectorAll(".shopify-section").forEach((s) => {
      s.style.opacity = "";
      s.style.transform = "";
      s.style.filter = "";
      s.style.willChange = "";
    });
  }

  function fadeInHomeLogo() {
    if (!document.documentElement.classList.contains("logo-intro")) return;

    const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!window.gsap || reduceMotion) {
      document.documentElement.classList.remove("logo-intro");
      return;
    }

    const el =
      document.querySelector("a.header-logo .header-logo__image-container--original") ||
      document.querySelector("a.header-logo .header-logo__image-container");

    if (!el) {
      requestAnimationFrame(fadeInHomeLogo);
      return;
    }

    const parent = el.closest("a.header-logo") || el.parentElement;
    if (!parent) return;

    // Create a CLEAN buddy element that the theme won't resize/override
    let buddy = parent.querySelector(".home-logo-buddy");
    if (!buddy) {
      buddy = document.createElement("span");
      buddy.className = "home-logo-buddy";
      buddy.setAttribute("aria-hidden", "true");
      parent.appendChild(buddy);
    }

    // Ensure buddy is hidden until Step 3
    gsap.set(buddy, { opacity: 0, x: 160, force3D: true });

    // failsafe (never leave things hidden)
    const failSafe = setTimeout(() => {
      document.documentElement.classList.remove("logo-intro");
      gsap.set(buddy, { opacity: 1, x: 0 });
    }, 4000);

    const tl = gsap.timeline({
      onComplete: () => {
        clearTimeout(failSafe);

        // lock buddy end state
        gsap.set(buddy, { opacity: 1, x: 0 });

        // lock real logo state
        el.style.setProperty("--intro-o", 0);
        el.style.setProperty("--intro-x", "-70px");
        el.style.setProperty("--real-o", 1);
        el.style.setProperty("--real-x", "0px");

        document.documentElement.classList.remove("logo-intro");

        // cleanup inline vars
        el.style.removeProperty("--intro-o");
        el.style.removeProperty("--intro-x");
        el.style.removeProperty("--real-o");
        el.style.removeProperty("--real-x");
      }
    });

    // 1) Intro logo slides in
    tl.to(el, {
      "--intro-x": "0px",
      "--intro-o": 1,
      duration: 1.4,
      ease: "power3.out"
    });

    // 2) Swap: intro out, real in
    tl.to(
      el,
      {
        "--intro-x": "-70px",
        "--intro-o": 0,
        "--real-x": "0px",
        "--real-o": 1,
        duration: 1.2,
        ease: "power3.inOut"
      },
      ">-0.1"
    );

    // 3) Buddy slides in RIGHT -> LEFT (only now becomes visible)
    tl.to(
      buddy,
      {
        opacity: 1,
        x: 0,
        duration: 1.0,
        ease: "power3.out",
        overwrite: "auto"
      },
      ">-0.15"
    );
  }

  function buildSections() {
    const main = getMain();
    if (!main) return;

    // If ScrollTrigger isn't loaded, skip section reveals
    if (!window.ScrollTrigger) return;

    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    const sections = getSections(main);

    sections.forEach((section, idx) => {
      if (section.hasAttribute("data-gsap-ready")) return;
      section.setAttribute("data-gsap-ready", "true");

      section.style.willChange = "transform, opacity";

      const s = state(section);
      if (s === "below") {
        gsap.set(section, { opacity: FROM_OPACITY, y: FROM_Y, force3D: true });
      } else {
        gsap.set(section, { opacity: 1, y: 0 });
      }

      const tween = gsap.to(section, {
        opacity: 1,
        y: 0,
        duration: IN_DURATION,
        ease: EASE,
        paused: true,
        overwrite: "auto"
      });

      ScrollTrigger.create({
        id: `home:${section.id || idx}`,
        trigger: section,
        start: "top 85%",
        end: "bottom 15%",
        animation: tween,
        toggleActions: "play none none reverse",
        invalidateOnRefresh: true
      });
    });

    ScrollTrigger.refresh(true);

    // Sync to current scroll (restore / load-at-bottom)
    ScrollTrigger.getAll().forEach((st) => {
      if (!String(st.vars?.id || "").startsWith("home:")) return;
      const tw = st.animation;
      if (!tw) return;
      tw.progress(st.progress > 0 ? 1 : 0).pause();
    });
  }

  function refreshHard() {
    if (!window.ScrollTrigger) return;
    ScrollTrigger.refresh(true);
    ScrollTrigger.update(true);
  }

  function boot() {
    cleanup(document);
    fadeInHomeLogo();
    buildSections();
    refreshHard();
    document.documentElement.classList.remove(PENDING_CLASS);
  }

  waitForGSAP(() => {
    requestAnimationFrame(boot);

    window.addEventListener("load", () => requestAnimationFrame(() => refreshHard()), {
      once: true
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => refreshHard());
    }

    document.addEventListener("shopify:section:load", () => requestAnimationFrame(boot));
    document.addEventListener("shopify:section:reorder", () => requestAnimationFrame(boot));

    window.addEventListener("pageshow", () => requestAnimationFrame(() => refreshHard()));
  });
})();
